import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import ReactDOM from 'react-dom';
import { Button, Typography, Flex, Badge } from '@app/ui';
import RenderMediaModal from '@app/components/common/modals/RenderMediaModal';
import '@app/components/common/modals/PostModal.scss';
import Icon, { CloseOutlined } from '@ant-design/icons';
import LinkOutlinedL from '@app/components/posts/icons/LinkOutlinedL';
import { getPlatformIconClasses } from '@app/allSocialMediaPosts/utils';
import classNames from '@app/ui/sports-components/utils/classNames';

const { Title } = Typography;

interface MediaItem {
    url: string;
    title?: string;
    autoplay?: boolean;
    muted?: boolean;
    loop?: boolean;
    width?: number;
    height?: number;
}

interface Media {
    cdnMediaUrl: string;
    carouselMediaUrls?: MediaItem[];
    title?: string;
    width?: number;
    height?: number;
    content: string;
    username: string;
    platformId: number;
    mediaUrl: string;
    url: string;
}

interface CarouselModalProps {
    title?: string;
    currentMediaId?: number;
    media: Media;
    onClose: () => void;
}

const isImage = (url?: string): boolean =>
    !!url?.match(/\.(jpg|png|gif|jpeg)(?=\?|$)/i);
const domNode = document.getElementById('carousel-modal-root');

const CarouselModal: React.FC<CarouselModalProps> = ({
    title,
    currentMediaId = 0,
    media,

    onClose,
}) => {
    const { t } = useTranslation('measurement');
    const mediaList = media.carouselMediaUrls
        ? media.carouselMediaUrls.map(url => ({
              url,
              autoplay: true,
              muted: true,
          }))
        : [
              {
                  url: media.cdnMediaUrl,
                  autoplay: true,
                  muted: true,
              },
          ];

    const [idx] = useState(currentMediaId);
    const mediaRef = React.useRef<HTMLVideoElement>(null);
    const [badgeTop, setBadgeTop] = useState(20);
    const [badgeCalculated, setBadgeCalculated] = useState(false);

    // const prev = () => idx > 0 && setIdx(idx - 1);
    // const next = () => idx < mediaList.length - 1 && setIdx(idx + 1);
    const _media = mediaList[idx];

    useEffect(() => {
        document.body.classList.add('-noscroll');
        return () => document.body.classList.remove('-noscroll');
    }, []);

    useEffect(() => {
        const items = document.querySelectorAll<HTMLElement>('.carousel-item');
        items[idx]?.scrollIntoView({ behavior: 'smooth', inline: 'center' });
    }, [idx]);

    useEffect(() => {
        // Calculate badge position based on media element height
        const calculateBadgePosition = () => {
            const mediaWrapper = document.querySelector(
                '.modal-v2-media-wrapper',
            );
            if (mediaWrapper) {
                const mediaHeight = mediaWrapper.clientHeight;
                // Position badge at the bottom of the media with some padding
                const newTop = mediaHeight - 30; // 30px from bottom
                setBadgeTop(Math.max(20, newTop)); // Ensure minimum top position
                setBadgeCalculated(true); // Mark as calculated
            }
        };

        // Reset calculation state when media changes
        setBadgeCalculated(false);

        // Calculate position after media loads
        const timer = setTimeout(calculateBadgePosition, 100);

        // Also recalculate on window resize
        const handleResize = () => {
            calculateBadgePosition();
        };
        window.addEventListener('resize', handleResize);

        return () => {
            clearTimeout(timer);
            window.removeEventListener('resize', handleResize);
        };
    }, [idx, _media.url]);

    if (!mediaList.length || !domNode) return null;

    const checkImgExt = isImage(_media.url as string);
    const checkVideoExt = !checkImgExt;

    // Create a post-like object for RenderMediaModal
    const postForRender = {
        cdnMediaUrl: _media.url,
        autoplay: _media.autoplay,
        muted: _media.muted,
    };

    const socialIconClasses = classNames(
        'gds-text--body-md',
        getPlatformIconClasses(media.platformId),
    );

    return ReactDOM.createPortal(
        <div className="modal-overlay modal-shown" onClick={onClose}>
            <div
                className="modal-v2-wrapper"
                onClick={e => e.stopPropagation()}
            >
                <Button
                    className="button-close-v2"
                    color="primary"
                    variant="outlined"
                    icon={<CloseOutlined />}
                    size={'middle'}
                    onClick={onClose}
                />
                <div
                    className="modal-v2 carousel-container"
                    style={{
                        background: '#fff',
                        borderRadius: 8,
                        boxShadow: '0 4px 16px rgba(0,0,0,0.15)',
                        padding: 16,
                        maxWidth: '520px',
                    }}
                >
                    {badgeCalculated ? (
                        <Badge.Ribbon
                            text={`@${media.username}`}
                            rootClassName="badge_ribbon--container"
                            style={{
                                top: badgeTop,
                                right: -10,
                            }}
                        >
                            {RenderMediaModal(
                                postForRender,
                                checkImgExt,
                                checkVideoExt,
                                mediaRef,
                                t,
                            )}
                        </Badge.Ribbon>
                    ) : (
                        RenderMediaModal(
                            postForRender,
                            checkImgExt,
                            checkVideoExt,
                            mediaRef,
                            t,
                        )
                    )}

                    {(title || media.title) && (
                        <Title level={5} className="carousel-modal__title">
                            {title || media.title}
                        </Title>
                    )}
                    <Flex
                        justify="space-between"
                        align="flex-start"
                        style={{
                            padding: '12px 0',
                            width: '100%',
                        }}
                    >
                        <Flex gap={8} align="flex-start" style={{ flex: 1 }}>
                            <i
                                className={socialIconClasses}
                                aria-hidden="true"
                                style={{
                                    color: 'rgba(0, 0, 0, 0.45)',
                                    paddingTop: '5px',
                                }}
                            />
                            <Typography.Text className="carousel-modal__media-count">
                                {media.content}
                            </Typography.Text>
                        </Flex>
                        <a
                            href={media.url as string}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="-block"
                            data-test="post-link"
                            style={{ flexShrink: 0, paddingTop: '5px' }}
                        >
                            <Icon component={LinkOutlinedL} />
                        </a>
                    </Flex>
                </div>
            </div>
        </div>,
        domNode,
    );
};

export default CarouselModal;
